import { useNavigate, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  PiClipboardText,
  PiGraduationCap,
  PiHandCoins,
  PiHeartbeat,
  PiWheelchair,
  PiCar,
} from 'react-icons/pi'

import { useKycStore } from '@store/useKycStore'
import { useUserStore } from '@/store/useUserStore'

import Button from '@/components/common/Button/Button'
import ProgressBar from '@components/common/ProgressBar/ProgressBar'
import MotionText from '@/components/common/Motion/MotionText'
import Radio from '@components/common/Radio/Radio'
import Checkbox from '@components/common/Checkbox/Checkbox'
import HeaderBack from '@/components/dashboard/Header/HeaderBack'
import { usePageSetup } from '@/hooks/usePageSetup'

import kycAPI from '@/services/api/kyc'
import type { InsuranceStatus } from '@/types/user'

const STATUS_OPTIONS: { value: InsuranceStatus; icon: React.ReactNode }[] = [
  { value: 'employment_contract', icon: <PiClipboardText className="h-6 w-6 text-zinc-900" /> },
  { value: 'student', icon: <PiGraduationCap className="h-6 w-6 text-zinc-900" /> },
  { value: 'old_age_pension', icon: <PiHandCoins className="h-6 w-6 text-zinc-900" /> },
  { value: 'disability_pension', icon: <PiHeartbeat className="h-6 w-6 text-zinc-900" /> },
  { value: 'participation_level_40', icon: <PiWheelchair className="h-6 w-6 text-zinc-900" /> },
  { value: 'self_insured', icon: <PiCar className="h-6 w-6 text-zinc-900" /> },
]

export const KycInsuranceStatusPage = () => {
  const { t } = useTranslation()
  usePageSetup(t('kyc.pageTitle.insuranceStatus'), { scroll: true })

  const user = useUserStore((state) => state.user)
  const setUser = useUserStore((state) => state.setUser)

  const progress = useKycStore((state) => state.progress)
  const setProgress = useKycStore((state) => state.setProgress)

  const [isLoading, setIsLoading] = useState(false)
  const [selectedStatus, setSelectedStatus] = useState<InsuranceStatus | ''>(user?.kyc?.insurance_status || '')
  const [firstYear, setFirstYear] = useState<boolean>(!!user?.kyc?.first_year_started_at)

  useEffect(() => setProgress(6), [])

  const navigate = useNavigate()
  const location = useLocation()
  // When opened from the profile or the tax dashboard, return there instead of continuing onboarding.
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!selectedStatus) return

    // Stamp the moment first-year was declared; keep the original timestamp if
    // already set so re-editing in a later year doesn't extend the VSD opt-out.
    const kycPatch = {
      insurance_status: selectedStatus,
      first_year_started_at: firstYear
        ? (user?.kyc?.first_year_started_at ?? new Date().toISOString())
        : null,
    }

    try {
      setIsLoading(true)
      const response = await kycAPI.create({ kyc: kycPatch })
      if (user) setUser({ ...user, kyc: response.kyc })
    } catch {
      // Backend may not yet persist insurance fields — keep the selection locally
      // so the onboarding flow and calculator still work.
      if (user) setUser({ ...user, kyc: { ...user.kyc, ...kycPatch } })
    }

    navigate(returnTo ?? '/kyc/mfa')
  }

  return (
    <form onSubmit={handleSubmit} className="h-full bg-white p-5 pb-8 flex flex-col">
      <HeaderBack className='items-center gap-4'>
        <ProgressBar
          currentStep={progress}
          totalSteps={8}
        />
      </HeaderBack>
      <div className="flex-1 flex flex-col mt-4">
        <div className="space-y-4">
          <MotionText
            as="h2"
            className="text-zinc-900 text-3xl font-bold leading-tight"
            delay={0.2}
          >
            {t('kyc.insuranceStatus.title')}
          </MotionText>
          <MotionText
            as="p"
            className="text-zinc-700 text-base"
            delay={0.4}
          >
            {t('kyc.insuranceStatus.description')}
          </MotionText>
        </div>
        <motion.div
          className='flex flex-col mt-8 mb-32 gap-3'
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            delay: 0.2,
            duration: 0.2,
            ease: "easeOut"
          }}
        >
          <div className="space-y-3">
            {STATUS_OPTIONS.map(({ value, icon }) => (
              <Radio
                key={value}
                required
                name="insuranceStatus"
                value={value}
                label={t(`kyc.insuranceStatus.options.${value}`)}
                className='pb-4 border-b border-zinc-200'
                icon={icon}
                checked={selectedStatus === value}
                onChange={(e) => setSelectedStatus(e.target.value as InsuranceStatus)}
              />
            ))}
          </div>

          <div className="pt-2">
            <Checkbox
              name="firstYear"
              checked={firstYear}
              onChange={(e) => setFirstYear(e.target.checked)}
              label={
                <span className="text-base font-medium text-zinc-900">
                  {t('kyc.insuranceStatus.firstYear')}
                </span>
              }
            />
          </div>
        </motion.div>
      </div>

      <div className="absolute bottom-0 ml-[-1.25rem] pl-5 pr-5 pb-8 w-full bg-linear-to-t from-white via-white to-transparent">
        <Button
          isLoading={isLoading}
          type='submit'
          disabled={isLoading || !selectedStatus}
          className={`h-12 w-full ${!selectedStatus ? '!bg-zinc-200 !text-zinc-500 cursor-not-allowed' : ''}`}
        >
          <motion.span
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              duration: 0.2,
              ease: "easeOut"
            }}
          >
            {t('common.continue')}
          </motion.span>
        </Button>
      </div>
    </form>
  )
}

export default KycInsuranceStatusPage
